// src/hooks/useVehicleStatuses.js
//
// Single source of truth for vehicle-status classifications across the whole
// app. Reads raw `state.devices.items` + raw `state.session.positions` +
// per-org thresholds, computes a Map<deviceId, VehicleStatus>, and memoizes
// against those three inputs.
//
// Before this hook existed, six surfaces (MapView, VehicleListPanel,
// TelemetryPanel, FleetStatusWidget, KpiRowWidget, StatusCardsWidget) each
// called getVehicleStatus directly. They mostly agreed, but during animation
// MapView read the *animator's* position object — which holds the PREVIOUS
// fix's fixTime/speed during a lerp leg — while the sidebar read raw Redux
// (latest fix). Inputs diverged for the duration of each animation, which
// could nudge the classification at the staleness boundary. Centralizing
// here makes the inputs identical by construction: the animator's lat/lng
// is purely presentational and never feeds the classifier.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getVehicleStatus } from '@/lib/utils';
import useStatusThresholds from '@/hooks/useStatusThresholds';

/**
 * @returns {Map<number, 'moving' | 'idle' | 'stopped' | 'offline' | 'pending'>}
 */
export default function useVehicleStatuses() {
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);
  const thresholds = useStatusThresholds();

  return useMemo(() => {
    const map = new Map();
    if (!devices) return map;
    for (const id in devices) {
      const device = devices[id];
      if (!device) continue;
      map.set(device.id, getVehicleStatus(device, positions?.[device.id], thresholds));
    }
    return map;
  }, [devices, positions, thresholds]);
}
